import React, {ReactNode, useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Row, RowLR, Select, Spinner, Text} from "react-vcomponents";
import {AssertUnreachable, Slider, TextPlus} from "web-vcore";
import {type ParamColor, type ParamKind, type ParamRamp, type ParamValue, type ParamValueInRecipe, type ParamVec3} from "../../../../../../Terrain/Materials/@MaterialTypes.js";
import {ColorPickerBox_Ext} from "../../../../../../Utils/ReactComponents/ColorPickerBox_Ext.js";
import {sliderFlex} from "../../../../../../Utils/UI/GlobalStyles.js";
import {RampEditor} from "../TerrainMaterials/RampEditor.js";

/** The MINIMAL resolved-param shape both editors can provide, abstracted over their concrete
 *  resolved-param types (`ResolvedParam` / `ResolvedSceneryParam`).  Only the fields this editor
 *  needs are declared, so either type is structurally assignable. */
export interface ParamEditData {
	key: string;
	label: string;
	kind: ParamKind;
	min: number;
	max: number;
	/** The value to show when this param INHERITS (`value == undefined`) — the resolved value one
	 *  inheritance level earlier (the root/intermediate override, NOT the current kind's own).  A
	 *  misnomer to call it "default": it's the fallback shown on inherit. */
	fallback: ParamValue;
	/** Set14 P2 — enum options (only for `kind == "enum"`). */
	options?: {value: string; label: string}[];
}

/** A 3-scalar editor (vec3 / color are both triples with different conventions).  `color` maps a
 *  0..1 tint; `vec3` is a signed direction/vector in any range.  Used by the shared editor. */
const TripleEditor = observer_mgl((props: {
	value: [number, number, number] | undefined;
	default: [number, number, number];
	onChange: (v: [number, number, number])=>void;
	min: number;
	max: number;
	/** Color components are 0..1 and shown as 0..255 swatches; vec3 are shown as raw sliders. */
	kind: "color" | "vec3";
})=>{
	const {value, default: dflt, onChange, min, max, kind} = props;
	const v = value ?? dflt;
	if (kind == "color") {
		return (
			<ColorPickerBox_Ext color={[...v.map(x=>Math.round(x * 255)), 1] as [number, number, number, number]}
				onChange={val=>onChange([val[0] / 255, val[1] / 255, val[2] / 255])}/>
		);
	}
	// vec3 — three sliders over [min, max] (signed direction/vector).
	const set = (i: number, val: number)=>{
		const next: [number, number, number] = [...v];
		next[i] = val;
		onChange(next);
	};
	return (
		<Row>
			{(["x", "y", "z"] as const).map((axis, i)=>(
				<Row key={axis} ml={i > 0 ? 5 : 0} style={{alignItems: "center"}}>
					<Text style={{minWidth: 14, fontSize: 10, opacity: 0.6}}>{axis}</Text>
					<Slider min={min} max={max} step={Math.max((max - min) / 200, 1e-4)}
						{...sliderFlex} value={v[i]} onChange={val=>set(i, val)}/>
				</Row>
			))}
		</Row>
	);
});

/** One param's VALUE editor, dispatching on the param's KIND (scalar/color/ramp/int/enum/bool/
 *  vec3) — the single shared control used by BOTH the material RecipeEditor and the scenery
 *  KindEditor.  `value` follows `ParamValueInRecipe`: `undefined` = inherit (default shown,
 *  reset disabled). */
export const ParamValueEditor = observer_mgl((props: {
	param: ParamEditData;
	value: ParamValueInRecipe;
	onChange: (v: ParamValueInRecipe)=>void;
	info?: string;
	title?: string;
	canReset?: boolean;
	resetTitle?: string;
	extraLabel?: ReactNode;
})=>{
	const {param, value: value_raw, onChange, extraLabel} = props;
	const [rampEditing, setRampEditing] = useState(false);
	const info = props.info ?? param.label;
	const title = props.title ?? param.key;
	const split = 180;
	const deleteButton = (enabled: boolean)=>(
		<Button ml={5} mdIcon="delete" title={props.resetTitle ?? "Reset to default"} enabled={enabled}
			onClick={()=>onChange(undefined)}/>
	);

	if (param.kind == "scalar") {
		// value = the user's override when numeric, else inherit → show the fallback in the slider.
		const value = typeof value_raw == "number" ? value_raw : undefined;
		const value_resolved = value ?? param.fallback as number;
		return (
			<RowLR mt={3} splitAt={split}>
				<TextPlus info={info} title={title}>{param.label}:{extraLabel}</TextPlus>
				<Slider min={param.min} max={param.max} step={Math.max((param.max - param.min) / 200, 1e-4)}
					{...sliderFlex} value={value_resolved} onChange={val=>onChange(val)}/>
				<Spinner ml={5} placeholder={param.fallback as any}
					value={value} onChange={val=>onChange(val)}/>
				{deleteButton(props.canReset ?? value !== undefined)}
			</RowLR>
		);
	}
	if (param.kind == "int") {
		// int — an integer slider + spinner. The stored value is always an integer (clamped to the
		// schema range); inheriting shows the (rounded) default.
		const value = typeof value_raw == "number" ? Math.round(value_raw) : undefined;
		const value_resolved = value ?? Math.round(param.fallback as number);
		return (
			<RowLR mt={3} splitAt={split}>
				<TextPlus info={info} title={title}>{param.label}:{extraLabel}</TextPlus>
				<Slider min={param.min} max={param.max} step={1}
					{...sliderFlex} value={value_resolved} onChange={val=>onChange(Math.round(val))}/>
				<Spinner ml={5} step={1} placeholder={String(Math.round(param.fallback as number))}
					value={value} onChange={val=>onChange(Math.round(val))}/>
				{deleteButton(props.canReset ?? value !== undefined)}
			</RowLR>
		);
	}
	if (param.kind == "bool") {
		// bool — a checkbox. `true`/`false` are the only carriers (never swung).
		const value = typeof value_raw == "boolean" ? value_raw : undefined;
		const value_resolved = value ?? !!param.fallback;
		return (
			<RowLR mt={3} splitAt={split}>
				<TextPlus info={info} title={title}>{param.label}:{extraLabel}</TextPlus>
				<CheckBox text="" value={value_resolved} onChange={val=>onChange(val)}/>
				{deleteButton(props.canReset ?? value !== undefined)}
			</RowLR>
		);
	}
	if (param.kind == "enum") {
		const options = param.options ?? [];

		// enum — a dropdown over the schema's `options` ({value, label}[]). Falling back to the
		// default when inheriting. ⚠ react-vcomponents' Select expects `{name, value}` options —
		// map the schema's {value, label} → {name, value} so the labels render (Select maps
		// non-{name,value} objects via toString(), which would show "[object Object]").
		const value = typeof value_raw == "string" ? value_raw : undefined;
		const value_resolved = value ?? (typeof param.fallback == "string" ? param.fallback : options[0]?.value ?? "");

		const selectOptions = options.map(o=>({name: o.label, value: o.value as any}));
		if (!selectOptions.some(a=>a.value == value)) {
			if (value == null) {
				selectOptions.push({name: `${value_resolved ?? ""} (fallback)`, value: value});
			} else {
				selectOptions.push({name: `${value ?? ""} (invalid)`, value: value});
			}
		}
		return (
			<RowLR mt={3} splitAt={split}>
				<TextPlus info={info} title={title}>{param.label}:{extraLabel}</TextPlus>
				<Select options={selectOptions} value={value} onChange={val=>onChange(val as string)}/>
				{deleteButton(props.canReset ?? value !== undefined)}
			</RowLR>
		);
	}
	if (param.kind == "vec3") {
		// vec3 — a signed 3-scalar editor (e.g. forceDirection). Never swung.
		const value = Array.isArray(value_raw) && value_raw.length == 3 && typeof value_raw[0] == "number" ? value_raw as ParamVec3 : undefined;
		const dflt = (Array.isArray(param.fallback) && param.fallback.length == 3 ? param.fallback : [0, 0, 0]) as ParamVec3;
		return (
			<RowLR mt={3} splitAt={split}>
				<TextPlus info={info} title={title}>{param.label}:{extraLabel}</TextPlus>
				<TripleEditor kind="vec3" min={param.min} max={param.max} value={value} default={dflt}
					onChange={val=>onChange(val)}/>
				{deleteButton(props.canReset ?? value !== undefined)}
			</RowLR>
		);
	}
	if (param.kind == "color") {
		// Param colors are 0..1; react-color's SketchPicker AND the swatch work in 0..255
		// (`rgba(...)`). Convert at the boundary so the stored value stays 0..1.
		const value = Array.isArray(value_raw) ? value_raw : undefined;
		const c = (value ?? param.fallback) as ParamColor;
		return (
			<RowLR mt={3} splitAt={split}>
				<TextPlus info={info} title={title}>{param.label}:{extraLabel}</TextPlus>
				<TripleEditor kind="color" min={0} max={1} value={value as ParamColor|undefined}
					default={[0, 0, 0]} onChange={val=>onChange([val[0], val[1], val[2]])}/>
				{deleteButton(props.canReset ?? value !== undefined)}
			</RowLR>
		);
	}
	if (param.kind == "ramp") {
		// ramp — the summary row shows the stop count, an "edit" checkbox toggling the ramp
		// editor (below), and the delete (reset-to-default) button.
		const value = Array.isArray(value_raw) ? value_raw : undefined;
		const stops = value ?? (Array.isArray(param.fallback) ? param.fallback : []) as ParamRamp;
		const summary = stops.length > 0 ? `${stops.length / 4} stop(s)` : "empty";
		return (
			<>
				<RowLR mt={3} splitAt={split}>
					<TextPlus info={info} title={title}>{param.label}:{extraLabel}</TextPlus>
					<Text style={{fontSize: 12, opacity: 0.7}}>{summary}</Text>
					<CheckBox ml={5} text="edit" value={rampEditing} onChange={val=>setRampEditing(val)}/>
					{deleteButton(props.canReset ?? value !== undefined)}
				</RowLR>
				{rampEditing && <RampEditor value={stops} onChange={onChange}/>}
			</>
		);
	}
	AssertUnreachable(param.kind);
});
